import { httpClient } from "@/shared/services/http";
import { TestImportFormValues } from "./test-import.types";
import axios from "axios";
import isValidationFailureResponse from "@/shared/utils/comparison/isValidationFailureResponse";
import { ValidationFailureResponse } from "@/shared/interfaces/validation-error.interface";

interface ImportSuccess {
  success: true;
  testId: number;
  testName: string;
}

type ImportResult = ImportSuccess | ValidationFailureResponse;

export class TestImportHttpService {
  /**
   * Creates a new test by uploading metadata + a ZIP bundle.
   *
   * The backend expects multipart/form-data with:
   *   - `metadata` — a JSON string of {@link TestImportFormValues} minus the `zipFile` field
   *   - `bundle`   — the ZIP file
   *
   * Returns the result directly. On validation failures (HTTP 400),
   * the Axios error response body contains the structured failure payload.
   */
  static async createTest(formValues: TestImportFormValues): Promise<ImportResult> {
    const formData = new FormData();
    const { zipFile, ...metadata } = formValues;
    formData.append("metadata", JSON.stringify(metadata));

    const file = zipFile?.originFileObj;
    if (!file) {
      throw new Error("ZIP file is required for test import");
    }
    formData.append("bundle", file);

    try {
      const { data } = await httpClient.post<ImportResult>("/api/tests/import", formData, {
        // Allow up to 5 minutes for large bundles with many images.
        // Do NOT set Content-Type manually — axios auto-detects FormData and
        // appends the required multipart boundary.
        timeout: 300_000,
      });
      return data;
    } catch (error: unknown) {
      if (
        axios.isAxiosError(error) &&
        error.response?.status === 400 &&
        isValidationFailureResponse(error.response.data)
      ) {
        return error.response.data;
      }
      throw error;
    }
  }
}
